import {
    fromTheme,
    nativeOnlyStyles,
    PlainLabel,
    styled,
    Tooltip,
    ValidationError,
    View,
    webOnlyStyles
} from 'lib_ui-primitives';
import { constants } from 'lib_ui-services';
import { forwardRef, createElement as rc, useMemo } from 'react';
import { useTheme } from 'styled-components';
import useReadButtonSelection from '../../hooks/useReadButtonSelection';
import BarcodeScanner from '../actionElement/BarcodeScanner';
import PowerSliderPopup from '../actionElement/PowerSliderPopup';
import SensorReadButton from '../actionElement/SensorReadButton';
import ReadProvider from '../contextProviders/ReadProvider';

const RfidButton = styled(SensorReadButton).attrs({
    name: 'rfid-button',
    alt: 'Scan for RFID',
    color: 'transparent'
})``;
RfidButton.displayName = 'RfidButton';

let Container = styled(View).attrs({ name: 'form-field' })`
    position: relative;
    max-width: ${({ noMaxWidth, theme }) =>
        noMaxWidth || theme.mobile ? 'unset' : fromTheme('form', 'entryMaxWidth')};
    min-width: ${fromTheme('form', 'entryMinWidth')};
    flex-basis: 1px;
    flex-grow: 1;

    ${({ theme }) =>
        theme.selectedTheme !== 'gl2025' && {
            marginTop: 0,
            marginBottom: theme.textMargin,
            marginRight: theme.textMargin * 2
        }}

    color: ${fromTheme('defaultFontColor')};
    font-family: ${fromTheme('font')};
    font-size: ${fromTheme('fontSize')};
    & input {
        width: 100%;
    }
`;
Container = webOnlyStyles(Container)`
    display: block;
    overflow: visible;
    grid-column: ${({ noMaxWidth }) => (noMaxWidth ? '1 / -1' : 'auto')};
`;
Container = nativeOnlyStyles(Container)`
    display: flex;
    flex-direction: column;
    min-height: ${fromTheme('form', 'entryMinHeight')};
    max-height: ${({ hasErrors }) => (hasErrors ? 'none' : fromTheme('form', 'entryMaxHeight'))};
`;
Container.displayName = 'Container';

const ScanLabelAndField = styled(View).attrs({ name: 'scan-label-and-field' })`
    flex-direction: row;
    overflow: visible;
`;
ScanLabelAndField.displayName = 'ScanLabelAndField';

const LabelAndFieldGl2025 = styled(View).attrs({ name: 'label-and-field' })`
    display: flex;
    flex-direction: ${({ $labelBesideField }) => ($labelBesideField ? 'row' : 'column')};
    align-items: ${({ $labelBesideField }) => ($labelBesideField ? 'center' : 'flex-start')};
    justify-content: ${({ $labelBesideField }) => $labelBesideField && 'space-between'};
    gap: ${fromTheme('input', 'container', 'gap')};
    column-gap: ${({ $labelBesideField }) => $labelBesideField && '24px'};
    padding-bottom: ${fromTheme('formField', 'padding', 'bottom')};
    padding-right: ${fromTheme('formField', 'padding', 'right')};
    padding-left: ${fromTheme('formField', 'padding', 'left')};
    width: 100%;
`;
LabelAndFieldGl2025.displayName = 'LabelAndField';

let LabelAndFieldOld = styled(View).attrs({ name: 'label-and-field' })`
    position: relative;
    ${({ $labelBesideField }) => $labelBesideField && 'display: flex; flex-direction: row; align-items: center;'}
    justify-content: ${({ $labelBesideField }) => $labelBesideField && 'space-between'};
    column-gap: ${({ $labelBesideField }) => $labelBesideField && '24px'};
    font-size: 16px;
    margin: 0;
    padding: 14px 0 4px;
`;
LabelAndFieldOld = webOnlyStyles(LabelAndFieldOld)`
    width: 100%;
    display: ${({ $labelBesideField }) => ($labelBesideField ? 'flex' : 'inline-block')};
    box-sizing: border-box;
    overflow: visible;
`;
LabelAndFieldOld = nativeOnlyStyles(LabelAndFieldOld)`
    position: static;
    display: flex;
    flex-direction: ${({ $labelBesideField }) => ($labelBesideField ? 'row' : 'column')};
    align-items: ${({ $labelBesideField }) => ($labelBesideField ? 'center' : 'flex-start')};
    width: 100%;
    overflow: visible;
    padding: 0;
    margin-bottom: ${({ needsErrorSpacing, theme }) => (needsErrorSpacing ? theme.textMargin + 'px' : '0')};
`;
LabelAndFieldOld.displayName = 'LabelAndField';

const LabelOldNative = styled(PlainLabel)`
    width: ${({ $labelBesideField }) => ($labelBesideField ? 'auto' : '100%')};
    margin-bottom: ${({ $labelBesideField }) => ($labelBesideField ? '0' : fromTheme('textMargin'))};
    font-size: ${({ theme }) => theme.fontSizeLabel + 'px'};
    text-align: left;
    color: ${({ active, theme, disabled, empty }) =>
        active
            ? theme.colorScheme.primary
            : disabled
              ? theme.disabledLabelColor
              : empty
                ? theme.labelForEmptyField
                : theme.disabledLabelColor};
`;
LabelOldNative.displayName = 'LabelOldNative';

let Field = styled(View).attrs({ name: 'field-container' })`
    display: flex;
    align-items: center;
    flex-grow: ${({ $labelBesideField }) => ($labelBesideField ? 0 : 1)};
    width: ${({ $labelBesideField }) => ($labelBesideField ? 'auto' : '100%')};

    & > div {
        display: flex;
    }
`;
Field = nativeOnlyStyles(Field)`
    max-height: ${({ hasErrors }) => (hasErrors ? 'none' : '42px')};
`;
Field.displayName = 'Field';

const hasValidationErrors = errors => {
    if (Array.isArray(errors)) {
        return errors.length > 0;
    }
    return Boolean(errors);
};

const needsLegacyNativeErrorSpacing = ({ native, selectedTheme, hasErrors }) => native && selectedTheme !== 'gl2025' && hasErrors;
const usesLegacyNativeStackedLabel = props => needsLegacyNativeErrorSpacing(props);
const shouldFloatLegacyLabel = ({ active, fieldEmpty }) => active || !fieldEmpty;

// TODO: It would be better to calculate the label top below based on font size
//  textMargin and borders.  If theme values for those change, this will likely be messed up.
let LabelOld = styled(PlainLabel)`
    bottom: 0;
    left: ${({ floating }) => (floating ? '0' : '4px')};
    right: 0;
    top: ${({ floating }) => (floating ? '4px' : '24px')};
    font-size: ${({ floating, theme }) => (floating ? theme.fontSizeLabel + 'px' : theme.fontSize + 'px')};
    position: ${({ $labelBesideField }) => ($labelBesideField ? 'static' : 'absolute')};
    width: ${({ $labelBesideField }) => ($labelBesideField ? 'auto' : '100%')};
    overflow: hidden;
    text-align: left;
    color: ${({ active, floating, theme, disabled, empty }) =>
        active
            ? theme.colorScheme.primary
            : floating
              ? theme.disabledLabelColor
              : disabled
                ? theme.disabledLabelColor
                : empty
                  ? theme.labelForEmptyField
                  : theme.labelColor};
`;
LabelOld = webOnlyStyles(LabelOld)`
    display: block;
    white-space: nowrap;
    pointer-events: none;
    transition-duration: 0.2s;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
`;

const LabelGl2025 = styled(PlainLabel)`
    ${fromTheme('input', 'label', 'font')};
    color: ${({ disabled }) =>
        disabled ? fromTheme('input', 'label', 'color', 'disabled') : fromTheme('input', 'label', 'color', 'default')};
`;

const RightAlignedTooltip = styled(View)`
    position: absolute;
    right: 0;
    top: 0;
`;

const LabelWithInlineTooltip = styled(View).attrs({ name: 'label-with-tooltip' })`
    flex-direction: row;
    align-items: center;
    column-gap: 8px;
    min-width: 0;
`;

// A wrapped label must give up width, not squeeze the icon.
const InlineTooltip = styled(View)`
    flex-shrink: 0;
`;

// With the label beside the field (e.g. Toggle) the icon sits right after the label, not over the field.
const withTooltip = (label, { tooltip, native, labelBesideField }) => {
    if (native || !tooltip) return [label];
    if (labelBesideField) {
        return [rc(LabelWithInlineTooltip, null, label, rc(InlineTooltip, null, rc(Tooltip, { title: tooltip })))];
    }
    return [label, rc(RightAlignedTooltip, null, rc(Tooltip, { title: tooltip }))];
};

const _p = {
    RfidButton,
    hasValidationErrors,
    needsLegacyNativeErrorSpacing,
    usesLegacyNativeStackedLabel,
    shouldFloatLegacyLabel
};
export const _private = _p;
const EMPTY_HNODE = {};
export default forwardRef(function FormField(props, ref) {
    const { native, selectedTheme } = useTheme();
    const {
        hNode = EMPTY_HNODE,
        title,
        testID,
        children,
        active,
        errors,
        fieldEmpty,
        labelProps,
        className,
        disabled,
        style,
        onBlur,
        displayScanButton: _displayScanButton,
        displayRfidPowerButton: _displayRfidPowerButton,
        scanRfid: _scanRfid,
        scanBarcode: _scanBarcode,
        id: _id,
        tooltip: _tooltip,
        currentRoute,
        noMaxWidth = false,
        labelBesideField = false,
        required = false
    } = props || { hNode: {} };

    // Allow these values to be passed in via hNode or individually.
    const modifiedHNode = {
        displayScanButton: _displayScanButton ?? hNode.displayScanButton ?? false,
        displayRfidPowerButton: _displayRfidPowerButton ?? hNode.displayRfidPowerButton ?? false,
        scanRfid: _scanRfid ?? hNode.scanRfid ?? false,
        scanBarcode: _scanBarcode ?? hNode.scanBarcode ?? false
    };

    // Calculate if we should display these buttons
    const { displayRfidButton, displayBarcodeButton, displayRfidPowerButton } = useReadButtonSelection(modifiedHNode);
    const hasErrors = hasValidationErrors(errors);
    const needsErrorSpacing = needsLegacyNativeErrorSpacing({ native, selectedTheme, hasErrors });
    const usesStackedLabel = usesLegacyNativeStackedLabel({ native, selectedTheme, hasErrors });
    const shouldFloatLabel = shouldFloatLegacyLabel({ active, fieldEmpty, native, selectedTheme, hasErrors });

    let id = _id ?? hNode.id;
    let tooltip = _tooltip ?? hNode.tooltip;
    const desiredSensorTypes = useMemo(() => {
        const _sensorTypes = [];
        if (modifiedHNode.scanRfid) {
            _sensorTypes.push(constants.sensorTypes.RFID);
        }
        if (modifiedHNode.scanBarcode) {
            _sensorTypes.push(constants.sensorTypes.BARCODE);
        }
        return _sensorTypes;
    }, [modifiedHNode.scanRfid, modifiedHNode.scanBarcode]);

    // prettier-ignore
    return rc(ReadProvider, { id: `${title}${id}`, desiredSensorTypes, active },
        rc(Container, { className, ref, onBlur, style, noMaxWidth, hasErrors },
            rc(ScanLabelAndField, null,
                displayRfidButton && rc(_p.RfidButton, {
                    ref,
                    id: 'rfidScanner' + id,
                    key: 'rfidScanner' + id,
                    'data-testid': 'rfidScannerButton' + id,
                    disabled,
                    tabIndex: -1
                }),
                displayRfidPowerButton && rc(PowerSliderPopup, {id, sensorTypesAvailable: desiredSensorTypes, currentRoute}),
                displayBarcodeButton && rc(BarcodeScanner, {
                    ref,
                    id: 'scanner' + id,
                    key: 'scanner' + id,
                    tabIndex: -1
                }),
                selectedTheme === 'gl2025' && rc(LabelAndFieldGl2025, { $labelBesideField: labelBesideField },
                    ...withTooltip(rc(LabelGl2025, {
                        htmlFor: id,
                        floating: active || !fieldEmpty,
                        empty: fieldEmpty,
                        active,
                        disabled,
                        ...labelProps,
                        testID: `fieldLabel${testID ?? id}`,
                        // Needed for Appium testing
                        accessibility: true,
                        accessibilityLabel: `fieldLabel${testID ?? id}`
                    }, (title ?? '') + (required ? '*' : '')), { tooltip, native, labelBesideField }),
                    rc(Field, { hasErrors, $labelBesideField: labelBesideField }, children)
                ),
                selectedTheme !== 'gl2025' && rc(LabelAndFieldOld, { needsErrorSpacing, $labelBesideField: labelBesideField },
                    ...withTooltip(usesStackedLabel
                        ? rc(LabelOldNative, {
                              $labelBesideField: labelBesideField,
                              htmlFor: id,
                              empty: fieldEmpty,
                              active,
                              disabled,
                              ...labelProps,
                              testID: `fieldLabel${testID ?? id}`,
                              accessibility: true,
                              accessibilityLabel: `fieldLabel${testID ?? id}`
                          }, (title ?? '') + (required ? '*' : ''))
                        : rc(LabelOld, {
                            $labelBesideField: labelBesideField,
                              htmlFor: id,
                            floating: shouldFloatLabel,
                              empty: fieldEmpty,
                              active,
                              disabled,
                              ...labelProps,
                              testID: `fieldLabel${testID ?? id}`,
                              // Needed for Appium testing
                              accessibility: true,
                              accessibilityLabel: `fieldLabel${testID ?? id}`
                          }, (title ?? '') + (required ? '*' : '')), { tooltip, native, labelBesideField }),
                    rc(Field, { hasErrors, $labelBesideField: labelBesideField }, children)
                )
            ),
            rc(ValidationError, { errors })
        )
    );
});
